import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { DASHBOARD_WIDGETS } from '../config/dashboardWidgets';

/**
 * Redirects to the first allowed route when the user lacks permission.
 */
export function ProtectedRoute({ routeKey, children, fallback }) {
  const { canAccessRoute, loading } = useAuth();
  const defaultRoute = useDefaultRoute();

  if (loading) {
    return null;
  }

  if (!canAccessRoute(routeKey)) {
    return <Navigate to={fallback || defaultRoute} replace />;
  }

  return children;
}

/**
 * Picks the best default landing path for the signed-in role.
 */
export function useDefaultRoute() {
  const { canAccessRoute } = useAuth();

  if (canAccessRoute('dashboard')) return '/dashboard';
  if (canAccessRoute('clients')) return '/clients';
  return '/clients';
}

/**
 * Shown when the user can open the dashboard route but has no widget permissions.
 */
export function DashboardEmptyState() {
  return (
    <Box sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" gutterBottom>
        No dashboard widgets assigned
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Your role does not include any dashboard widgets. Contact an administrator to update your permissions.
      </Typography>
    </Box>
  );
}

export function useHasAnyDashboardWidget() {
  const { canViewWidget } = useAuth();
  return DASHBOARD_WIDGETS.some((widget) => canViewWidget(widget.id));
}

export default ProtectedRoute;
